import React, { ReactElement, useState } from 'react'
import { useTranslate } from 'src/localization/Trns'

import SvgCopy from '../icons/SvgCopy'
import type {
  CardRevealDetails,
  CardRevealState,
} from '../useCardRevealDisclosure'
import * as style from './CardRevealPanel.style'

type CardRevealPanelProps = {
  /** Ties the panel to the Show details trigger via aria-controls. */
  id: string
} & (
  | {
      state: Exclude<CardRevealState, 'idle' | 'ready'>
      details?: never
    }
  | ({
      state: 'ready'
    } & CardRevealDetails)
)

// Resolves once the text is on the clipboard, rejects otherwise — a missing
// Clipboard API, a denied clipboard-write permission, or an unfocused document
// all reject. Callers must handle the rejection: a bare `void` would leak an
// unhandledrejection and still leave the user with no idea the copy failed.
const writeToClipboard = (text: string): Promise<void> => {
  if (!navigator.clipboard?.writeText) {
    return Promise.reject(new Error('Clipboard API unavailable'))
  }
  return Promise.resolve(navigator.clipboard.writeText(text))
}

const CopyButton = ({
  label,
  ariaLabel,
  value,
}: {
  label: string
  ariaLabel?: string
  value: string
}): ReactElement => {
  const translate = useTranslate()
  const [failed, setFailed] = useState(false)

  const onCopy = (): void => {
    setFailed(false)
    writeToClipboard(value).catch(() => setFailed(true))
  }

  return (
    <span css={style.copyGroup}>
      <button
        type="button"
        css={style.copyButton}
        aria-label={ariaLabel}
        onClick={onCopy}
      >
        <SvgCopy width={13} height={13} />
        {label}
      </button>
      {failed && (
        <span role="alert" css={style.copyError}>
          {translate("Couldn't copy — select and copy manually")}
        </span>
      )}
    </span>
  )
}

/**
 * The card-details reveal disclosure — card number, expiration, and security
 * code, with copy actions for the protected values. Not a modal: it is a
 * labelled region tied to the Show details trigger by id, so the page stays
 * interactive. Its open/close (Escape, outside click, timeout) is owned by the
 * shell.
 */
const CardRevealPanel: React.FC<CardRevealPanelProps> = (
  props,
): ReactElement => {
  const translate = useTranslate()

  if (props.state !== 'ready') {
    const stateText = (() => {
      switch (props.state) {
        case 'loading':
          return translate('Loading secure card details')
        case 'unavailable':
          return translate('Card details are unavailable for this card')
        case 'error':
          return translate(
            'Card details could not be loaded. Hide details and try again.',
          )
      }
    })()

    return (
      <div id={props.id} css={style.panel} data-testid="card-reveal-panel">
        <div
          css={style.stateMessage}
          // Only `error` carries a live role here, and deliberately so.
          //
          // `alert` is the one live role specified to announce on *insertion*,
          // which is exactly how this panel arrives — mounted with its text
          // already in place. It also earns the interruption: an error is a
          // failure of something the user just asked for.
          //
          // `loading` and `unavailable` are announced by the hero's persistent
          // region instead. A `status` inserted alongside its own text is
          // unreliable across NVDA/JAWS/VoiceOver, and duplicating it here would
          // only make the shell's announcement land twice.
          role={props.state === 'error' ? 'alert' : undefined}
        >
          {stateText}
        </div>
      </div>
    )
  }

  const { cvc, expiry, pan } = props

  return (
    <div id={props.id} css={style.panel} data-testid="card-reveal-panel">
      <div css={style.footer}>
        <CopyButton label={translate('Copy card number')} value={pan} />
        <span css={style.detailGroup}>
          <span css={style.detailLabel}>{translate('Expires')}</span>
          <span css={style.detailValue} data-testid="card-reveal-expiry">
            {expiry}
          </span>
        </span>
        <span css={style.detailGroup}>
          <CopyButton
            label={translate('CVC')}
            ariaLabel={translate('Copy CVC')}
            value={cvc}
          />
          <span css={style.detailValue} data-testid="card-reveal-cvc">
            {cvc}
          </span>
        </span>
      </div>
    </div>
  )
}

export default CardRevealPanel
